import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { api } from '../lib/api';
import { usePriceFeed } from '../hooks/useWebSocket';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart
} from 'recharts';
import {
  Users, DollarSign, TrendingUp, Activity, Clock, Shield, Wifi, WifiOff, RefreshCw, Zap
} from 'lucide-react';
import QuickTrade from '../components/QuickTrade';

const MetricCard = ({ title, value, icon: Icon, change, className, delay, testId }) => (
  <Card className={`card-hover animate-fade-in-up ${className}`} style={{ animationDelay: `${delay}ms` }} data-testid={testId}>
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{title}</p>
          <p className="text-2xl font-bold font-mono tabular-nums">{value}</p>
          {change && (
            <p className={`text-xs mt-1 ${change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {change >= 0 ? '+' : ''}{change}% today
            </p>
          )}
        </div>
        <div className="p-3 rounded-xl bg-primary/10">
          <Icon className="w-5 h-5 text-primary" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [chartPeriod, setChartPeriod] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Live price feed via WebSocket
  const { prices: livePrices, isConnected: wsConnected } = usePriceFeed();

  const fetchData = async () => {
    try {
      const [metricsRes, chartRes] = await Promise.all([
        api.getDashboardMetrics(),
        api.getChartData(chartPeriod)
      ]);
      setMetrics(metricsRes.data);
      setChartData(chartRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [chartPeriod]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">Real-time overview of your brokerage</p>
        </div>
        <div className="flex items-center gap-3">
          {/* WebSocket Status */}
          {wsConnected && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10">
              <Zap className="w-4 h-4 text-emerald-400 animate-pulse" />
              <span className="text-xs text-emerald-400">Live Feed</span>
            </div>
          )}
          {/* MT5 Status */}
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
            metrics?.mt5_status === 'connected' ? 'bg-emerald-500/10' : 'bg-amber-500/10'
          }`}>
            {metrics?.mt5_status === 'connected' ? (
              <>
                <Wifi className="w-4 h-4 text-emerald-400" />
                <span className="text-xs text-emerald-400">MT5 Connected</span>
              </>
            ) : (
              <>
                <WifiOff className="w-4 h-4 text-amber-400 animate-pulse-live" />
                <span className="text-xs text-amber-400">MT5 Mock Mode</span>
              </>
            )}
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-dashboard-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Live Price Ticker */}
      {Object.keys(livePrices).length > 0 && (
        <Card className="overflow-hidden animate-fade-in-up">
          <CardContent className="p-0">
            <div className="flex items-center gap-6 px-4 py-3 overflow-x-auto">
              <span className="text-xs uppercase tracking-wider text-muted-foreground whitespace-nowrap flex items-center gap-2">
                <Zap className="w-3 h-3 text-primary" />
                Live Prices
              </span>
              {Object.entries(livePrices).slice(0, 7).map(([symbol, data]) => (
                <div key={symbol} className="flex items-center gap-2 whitespace-nowrap">
                  <span className="font-medium text-sm">{symbol}</span>
                  <span className="font-mono text-sm">{data.bid.toFixed(symbol.includes('JPY') ? 3 : symbol.includes('XAU') || symbol.includes('BTC') ? 2 : 5)}</span>
                  <span className={`text-xs font-mono ${data.change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {data.change >= 0 ? '+' : ''}{data.change}%
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Clients"
          value={metrics?.total_clients || 0}
          icon={Users}
          delay={0}
          testId="metric-total-clients"
        />
        <MetricCard
          title="Total Balance"
          value={formatCurrency(metrics?.total_balance || 0)}
          icon={DollarSign}
          change={2.4}
          delay={100}
          testId="metric-total-balance"
        />
        <MetricCard
          title="Total Equity"
          value={formatCurrency(metrics?.total_equity || 0)}
          icon={TrendingUp}
          change={metrics?.total_profit_today >= 0 ? 1.8 : -1.2}
          delay={200}
          testId="metric-total-equity"
        />
        <MetricCard
          title="Open Positions"
          value={metrics?.open_positions || 0}
          icon={Activity}
          delay={300}
          testId="metric-open-positions"
        />
      </div>

      {/* Secondary metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="animate-fade-in-up animation-delay-400">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-emerald-500/10">
              <DollarSign className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Today's P&L</p>
              <p className={`text-xl font-bold font-mono tabular-nums ${
                (metrics?.total_profit_today || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'
              }`}>
                {(metrics?.total_profit_today || 0) >= 0 ? '+' : ''}
                {formatCurrency(metrics?.total_profit_today || 0)}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="animate-fade-in-up animation-delay-500">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-amber-500/10">
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Pending KYC</p>
              <p className="text-xl font-bold font-mono tabular-nums">{metrics?.pending_kyc || 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="animate-fade-in-up animation-delay-500">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-blue-500/10">
              <Shield className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Active Traders</p>
              <p className="text-xl font-bold font-mono tabular-nums">{metrics?.active_traders || 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chart and Quick Trade */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <Card className="lg:col-span-3 animate-fade-in-up animation-delay-300">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg">Equity & Balance Overview</CardTitle>
            <Select value={chartPeriod} onValueChange={setChartPeriod}>
              <SelectTrigger className="w-24" data-testid="chart-period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">7 Days</SelectItem>
                <SelectItem value="30d">30 Days</SelectItem>
                <SelectItem value="90d">90 Days</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]" data-testid="equity-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00E5FF" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#00E5FF" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorEquity" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#CCFF00" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#CCFF00" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#52525B" 
                    fontSize={12}
                    tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis 
                    stroke="#52525B" 
                    fontSize={12}
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#0A0A0A', 
                      border: '1px solid #27272A',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                    formatter={(value) => [`$${value.toLocaleString()}`, '']}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="balance" 
                    stroke="#00E5FF" 
                    fillOpacity={1} 
                    fill="url(#colorBalance)"
                    strokeWidth={2}
                    name="Balance"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="equity" 
                    stroke="#CCFF00" 
                    fillOpacity={1} 
                    fill="url(#colorEquity)"
                    strokeWidth={2}
                    name="Equity"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-center gap-6 mt-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#00E5FF]" />
                <span className="text-sm text-muted-foreground">Balance</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#CCFF00]" />
                <span className="text-sm text-muted-foreground">Equity</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Trade Widget */}
        <div className="lg:col-span-1">
          <QuickTrade 
            livePrices={livePrices} 
            onTradeExecuted={() => fetchData()}
          />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
